import maplibregl, {
  type ExpressionSpecification,
  type GeoJSONSource,
  type Map as MapLibreMap,
  type StyleSpecification,
} from 'maplibre-gl';
import type { FeatureCollection, MultiLineString } from 'geojson';
import { hasPhotoLocation } from '../../domain/trail';
import type { Photo, RecordedRoute } from '../../schemas';

export const terrainStyle: StyleSpecification = {
  version: 8,
  name: 'Aotearoa relief',
  projection: { type: 'mercator' },
  sources: {
    satellite: {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      attribution: 'Imagery © Esri and contributors',
      maxzoom: 18,
    },
    dem: {
      type: 'raster-dem',
      tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
      tileSize: 256,
      encoding: 'terrarium',
      attribution: 'Elevation © AWS Open Data / Mapzen',
      maxzoom: 15,
    },
  },
  layers: [
    {
      id: 'satellite',
      type: 'raster',
      source: 'satellite',
      paint: {
        'raster-saturation': -0.28,
        'raster-contrast': 0.18,
        'raster-brightness-min': 0.12,
        'raster-brightness-max': 0.92,
      },
    },
    {
      id: 'hillshade',
      type: 'hillshade',
      source: 'dem',
      paint: {
        'hillshade-exaggeration': 0.46,
        'hillshade-shadow-color': '#182d22',
        'hillshade-highlight-color': '#f4e7bd',
        'hillshade-accent-color': '#7d8f64',
      },
    },
  ],
  sky: { 'sky-color': '#e8e2d2', 'sky-horizon-blend': 0.7, 'horizon-color': '#d8ddcf' },
};

export const nzView = {
  center: [170.7, -44.25] as [number, number],
  zoom: 5.55,
  pitch: 42,
  bearing: -12,
};

export type LocatedPhoto = Photo & { lat: number; lon: number };
export type PhotoMarkerCluster = {
  id: string;
  anchor: LocatedPhoto;
  photos: LocatedPhoto[];
};

function evenlySample<T>(items: T[], limit: number): T[] {
  if (items.length <= limit) return items;
  return Array.from(
    { length: limit },
    (_, index) => items[Math.round((index / (limit - 1)) * (items.length - 1))],
  ).filter((item): item is T => item !== undefined);
}

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function distanceBetweenPhotos(first: LocatedPhoto, second: LocatedPhoto): number {
  const earthRadiusM = 6_371_000;
  const latitudeDelta = toRadians(second.lat - first.lat);
  const longitudeDelta = toRadians(second.lon - first.lon);
  const latitudeA = toRadians(first.lat);
  const latitudeB = toRadians(second.lat);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(latitudeA) * Math.cos(latitudeB) * Math.sin(longitudeDelta / 2) ** 2;
  return 2 * earthRadiusM * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
}

export function clusterPhotosByLocation(photos: LocatedPhoto[], radiusM = 20): PhotoMarkerCluster[] {
  const photosByDate = new Map<string, LocatedPhoto[]>();
  for (const photo of photos) {
    const dayPhotos = photosByDate.get(photo.capture_date) ?? [];
    dayPhotos.push(photo);
    photosByDate.set(photo.capture_date, dayPhotos);
  }

  return [...photosByDate.values()].flatMap((dayPhotos) => {
    const clusters: LocatedPhoto[][] = [];
    for (const photo of dayPhotos) {
      const cluster = clusters.find((candidate) => {
        const anchor = candidate[0];
        return anchor ? distanceBetweenPhotos(anchor, photo) <= radiusM : false;
      });
      if (cluster) cluster.push(photo);
      else clusters.push([photo]);
    }

    return clusters.flatMap((clusterPhotos) => {
      const anchor = clusterPhotos.find((photo) => photo.orientation === 'landscape') ?? clusterPhotos[0];
      const orderedPhotos = anchor
        ? [anchor, ...clusterPhotos.filter((photo) => photo.id !== anchor.id)]
        : [];
      return anchor ? [{ id: anchor.id, anchor, photos: orderedPhotos }] : [];
    });
  });
}

export function clusterPhotoMarkersOnScreen(
  map: MapLibreMap,
  markers: PhotoMarkerCluster[],
  radiusPx: number,
): PhotoMarkerCluster[][] {
  const projected = new Map(
    markers.map((marker) => [marker.id, map.project([marker.anchor.lon, marker.anchor.lat])]),
  );
  const clusters: PhotoMarkerCluster[][] = [];

  for (const marker of markers) {
    const point = projected.get(marker.id);
    if (!point) continue;
    const cluster = clusters.find((candidate) => {
      const anchor = candidate[0];
      const anchorPoint = anchor ? projected.get(anchor.id) : undefined;
      return anchorPoint ? point.dist(anchorPoint) <= radiusPx : false;
    });
    if (cluster) cluster.push(marker);
    else clusters.push([marker]);
  }

  return clusters;
}

export function photoClusterExpansionCamera(
  map: MapLibreMap,
  cluster: PhotoMarkerCluster[],
  radiusPx: number,
): { center: [number, number]; zoom: number } | null {
  const maximumExpansionZoom = 12;
  const currentZoom = map.getZoom();
  if (currentZoom >= maximumExpansionZoom - 0.15) return null;

  const projected = cluster.map((marker) => map.project([marker.anchor.lon, marker.anchor.lat]));
  let minimumDistancePx = Number.POSITIVE_INFINITY;
  for (const [index, point] of projected.entries()) {
    for (const otherPoint of projected.slice(index + 1)) {
      minimumDistancePx = Math.min(minimumDistancePx, point.dist(otherPoint));
    }
  }
  const requiredZoomDelta = Math.max(0.85, Math.log2((radiusPx + 10) / Math.max(1, minimumDistancePx)) + 0.2);
  const zoom = Math.min(maximumExpansionZoom, currentZoom + Math.min(2.25, requiredZoomDelta));
  if (zoom <= currentZoom + 0.15) return null;

  const coordinates = cluster.map((marker) => [marker.anchor.lon, marker.anchor.lat] as [number, number]);
  const firstCoordinate = coordinates[0];
  if (!firstCoordinate) return null;
  const bounds = coordinates.reduce(
    (result, coordinate) => result.extend(coordinate),
    new maplibregl.LngLatBounds(firstCoordinate, firstCoordinate),
  );
  const center = bounds.getCenter();
  return { center: [center.lng, center.lat], zoom };
}

export function markerDensityTiers<T extends { id: string }>(
  items: T[],
  coreLimit: number,
  mediumLimit: number,
): Map<string, 0 | 1 | 2> {
  const core = new Set(evenlySample(items, coreLimit).map((item) => item.id));
  const mediumCandidates = items.filter((item) => !core.has(item.id));
  const medium = new Set(
    evenlySample(mediumCandidates, Math.max(0, mediumLimit - core.size)).map((item) => item.id),
  );
  return new Map(items.map((item) => [item.id, core.has(item.id) ? 0 : medium.has(item.id) ? 1 : 2]));
}

function publicGeoJson(route: RecordedRoute): FeatureCollection<MultiLineString> {
  return { type: 'FeatureCollection', features: route.features } as FeatureCollection<MultiLineString>;
}

export function addRecordedRoute(map: MapLibreMap, route: RecordedRoute) {
  map.addSource('recorded-route', { type: 'geojson', data: publicGeoJson(route) });
  map.addLayer({
    id: 'recorded-route-shadow',
    type: 'line',
    source: 'recorded-route',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#2b160e',
      'line-width': ['interpolate', ['linear'], ['zoom'], 4, 4, 10, 8.6],
      'line-opacity': 0.58,
      'line-blur': 2.4,
    },
  });
  map.addLayer({
    id: 'recorded-route-line',
    type: 'line',
    source: 'recorded-route',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#ff6a2a',
      'line-width': ['interpolate', ['linear'], ['zoom'], 4, 1.9, 10, 5.2],
      'line-opacity': 0.98,
    },
  });
  map.addLayer({
    id: 'recorded-route-hit',
    type: 'line',
    source: 'recorded-route',
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': '#ff6a2a', 'line-width': 18, 'line-opacity': 0 },
  });
  map.addSource('elevation-hover', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  });
  map.addLayer({
    id: 'elevation-hover-halo',
    type: 'circle',
    source: 'elevation-hover',
    paint: {
      'circle-radius': 11,
      'circle-color': '#fff1d0',
      'circle-opacity': 0.72,
      'circle-blur': 0.45,
    },
  });
  map.addLayer({
    id: 'elevation-hover-point',
    type: 'circle',
    source: 'elevation-hover',
    paint: {
      'circle-radius': 5,
      'circle-color': '#ff6a2a',
      'circle-stroke-color': '#fff8e9',
      'circle-stroke-width': 2,
    },
  });
}

export function setElevationLocation(map: MapLibreMap, coordinate: [number, number] | null) {
  const source = map.getSource('elevation-hover') as GeoJSONSource | undefined;
  if (!source) return;
  source.setData({
    type: 'FeatureCollection',
    features: coordinate
      ? [{ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: coordinate } }]
      : [],
  });
}

export function setSelectedRoute(
  map: MapLibreMap,
  activityId: string | null,
  selectedDate: string | null = null,
) {
  if (!map.getLayer('recorded-route-line')) return;
  const selected = ['==', ['get', 'activity_id'], activityId];
  const earlier = ['<', ['to-string', ['get', 'date']], selectedDate];
  const sameDay = ['==', ['get', 'date'], selectedDate];
  const hasSelection = selectedDate !== null;
  const lineOpacity = hasSelection ? ['case', selected, 1, 0.7] : 0.98;
  const shadowOpacity = hasSelection ? ['case', selected, 0.42, 0.22] : 0.58;
  const lineWidth = hasSelection
    ? [
        'interpolate',
        ['linear'],
        ['zoom'],
        4,
        ['case', selected, 2.5, earlier, 1.2, sameDay, 1.6, 0.65],
        10,
        ['case', selected, 4.6, earlier, 2.2, sameDay, 3, 1.1],
      ]
    : ['interpolate', ['linear'], ['zoom'], 4, 1.9, 10, 5.2];
  map.setPaintProperty(
    'recorded-route-line',
    'line-opacity',
    lineOpacity as ExpressionSpecification | number,
  );
  map.setPaintProperty('recorded-route-line', 'line-width', lineWidth as ExpressionSpecification);
  map.setPaintProperty(
    'recorded-route-line',
    'line-color',
    (hasSelection
      ? ['case', selected, '#ff7a3d', earlier, '#b86b49', sameDay, '#d96a3b', '#8c766a']
      : '#ff6a2a') as ExpressionSpecification | string,
  );
  map.setPaintProperty(
    'recorded-route-shadow',
    'line-opacity',
    shadowOpacity as ExpressionSpecification | number,
  );
}

export function focusActivity(map: MapLibreMap, route: RecordedRoute, activityId: string, isMobile: boolean) {
  const feature = route.features.find((item) => item.properties.activity_id === activityId);
  if (!feature) return;
  const points = feature.geometry.coordinates.flat();
  const bounds = points.reduce(
    (result, coordinate) => result.extend(coordinate),
    new maplibregl.LngLatBounds(points[0], points[0]),
  );
  map.fitBounds(bounds, {
    padding: isMobile
      ? { top: 50, right: 24, bottom: Math.round(window.innerHeight * 0.48), left: 24 }
      : { top: 48, right: 470, bottom: 48, left: 48 },
    maxZoom: 11.5,
    duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 650,
  });
}

export function focusWholeRoute(map: MapLibreMap, route: RecordedRoute, isMobile: boolean, animate = true) {
  const points = route.features.flatMap((feature) => feature.geometry.coordinates.flat());
  const firstPoint = points[0];
  if (!firstPoint) return;
  const bounds = points.reduce(
    (result, coordinate) => result.extend(coordinate),
    new maplibregl.LngLatBounds(firstPoint, firstPoint),
  );
  const desktopLeftPadding = Math.min(560, Math.round(window.innerWidth * 0.48));
  map.fitBounds(bounds, {
    padding: isMobile
      ? { top: 76, right: 18, bottom: 218, left: 18 }
      : { top: 70, right: 70, bottom: 70, left: desktopLeftPadding },
    pitch: isMobile ? 24 : map.getPitch(),
    bearing: isMobile ? 38 : map.getBearing(),
    maxZoom: 6.5,
    duration: !animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 900,
  });
}

export function focusPhotos(map: MapLibreMap, photos: Photo[], isMobile: boolean) {
  const located = photos.filter(hasPhotoLocation);
  if (!located.length) return;
  const bounds = located.reduce(
    (result, photo) => result.extend([photo.lon, photo.lat]),
    new maplibregl.LngLatBounds([located[0]!.lon, located[0]!.lat], [located[0]!.lon, located[0]!.lat]),
  );
  map.fitBounds(bounds, {
    padding: isMobile
      ? { top: 50, right: 24, bottom: Math.round(window.innerHeight * 0.48), left: 24 }
      : { top: 48, right: 470, bottom: 48, left: 48 },
    maxZoom: 12,
    duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 650,
  });
}
